import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Info, Package, Sprout, ChevronLeft, ChevronRight } from "lucide-react";
import {
  Product,
  productService,
} from "../../../services/product/product.service";

interface CompositionObject {
  values?: string[];
}

interface UtilizationObject {
  values?: string[];
}

const PlantDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [plant, setPlant] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchPlant = async () => {
      try {
        if (!id) return;
        const plantsResponse = await productService.getProductsByName(id);
  
        if (plantsResponse.Products && plantsResponse.Products.length > 0) {
          const plantData = await productService.getProductById(plantsResponse.Products[0].id);
          setPlant(plantData || null);
        } else {
          setError("Plant not found");
        }
      } catch (error) {
        console.error("Error fetching plant:", error);
        setError("Failed to load plant details");
      } finally {
        setLoading(false);
      }
    };
  
    fetchPlant();
  }, [id]);

  const handlePrevImage = () => {
    if (!plant?.images) return;
    setCurrentImageIndex((prev) => 
      prev === 0 ? plant.images.length - 1 : prev - 1
    );
  };

  const handleNextImage = () => {
    if (!plant?.images) return;
    setCurrentImageIndex((prev) => 
      prev === plant.images.length - 1 ? 0 : prev + 1
    );
  };

  const renderComposition = () => {
    if (!plant) return <li>No plant details available.</li>;
    
    const compositionValues = plant.composition?.values;
    
    if (!compositionValues || !Array.isArray(compositionValues)) {
      return <li>No composition information available.</li>;
    }
  
    return compositionValues.map((item: string, index: number) => (
      <li key={index} className="text-gray-600 list-none">{item}</li>
    ));
  };

  const renderUtilization = () => {
    if (!plant) return <li>No plant details available.</li>;
    
    const utilizationValues = plant.utilization?.values;
    
    if (!utilizationValues || !Array.isArray(utilizationValues)) {
      return <li>No usage information available.</li>;
    }
  
    return utilizationValues.map((item: string, index: number) => (
      <li key={index} className="text-gray-600 list-none">{item}</li>
    ));
  };

  const renderImageSection = () => {
    if (!plant?.images || plant.images.length === 0) {
      return (
        <div className="h-96 md:h-full relative">
          <img
            src="/placeholder-image.jpg"
            alt="Placeholder"
            className="w-full h-full object-cover"
          />
        </div>
      );
    }

    const imageUrl = `${process.env.REACT_APP_API_URL}${plant.images[currentImageIndex].image}`;

    if (plant.images.length === 1) {
      return (
        <div className="h-96 md:h-full relative group">
          <img
            src={imageUrl}
            alt={plant.name}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
        </div>
      );
    }

    return (
      <div className="h-96 md:h-full relative group">
        <img
          src={imageUrl}
          alt={plant.name}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
        />
        
        <button
          onClick={handlePrevImage}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors duration-200 focus:outline-none"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={handleNextImage}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/70 transition-colors duration-200 focus:outline-none"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
        
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex space-x-2">
          {plant.images.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-2 h-2 rounded-full transition-colors duration-200 ${
                index === currentImageIndex
                  ? 'bg-white'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
        
        <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
      </div>
    );
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (error || !plant) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-gray-50">
        <div className="text-center bg-white p-8 rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {error || "Plant not found"}
          </h2>
          <button
            onClick={handleGoBack}
            className="inline-flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Kembali
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-50 bg-white shadow-sm backdrop-blur-sm bg-white/90">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleGoBack}
              className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Kembali
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="md:flex">
            <div className="md:w-1/2">
              {renderImageSection()}
            </div>

            <div className="md:w-1/2 p-8">
              <div className="flex flex-col h-full">
                <div>
                  {plant.category_name && (
                    <div className="text-sm font-bold text-green-700 uppercase mb-2 bg-green-100 px-2 py-1 rounded-full inline-block">
                      {plant.category_name}
                    </div>
                  )}
                  
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    {plant.name}
                  </h1>
                  <p className="text-gray-500 mb-4 italic">{plant.latin_name}</p>

                  <div className="prose prose-sm text-gray-600 mb-8">
                    <p className="leading-relaxed">{plant.description}</p>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg mb-6">
                    <div className="flex items-center">
                      <Sprout className="w-5 h-5 text-green-600 mr-2" />
                      <span className="text-green-800 font-medium">Tanaman Herbal</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <section className="mt-8 space-y-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Deskripsi Tanaman</h2>
            <div className="prose max-w-none">
              <p className="text-gray-600 leading-relaxed">
                {plant.description}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Informasi Botanis</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Package className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Habitat</h4>
                    <p className="text-gray-600">{plant.area_name}</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-gray-400 mt-1" />
                  <div>
                    <h4 className="font-medium text-gray-900">Familia</h4>
                    <p className="text-gray-600">{plant.familia}</p>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Bagian yang Dimanfaatkan</h4>
                  <p className="text-gray-600">{plant.part_used}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Cara Perkembangbiakan</h4>
                  <p className="text-gray-600">{plant.method_of_reproduction}</p>
                </div>
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Usia Panen</h4>
                  <p className="text-gray-600">{plant.harvest_age}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Manfaat & Penggunaan</h2>
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Khasiat
                </h3>
                <p className="text-gray-600 leading-relaxed">{plant.efficacy}</p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  Cara Penggunaan
                </h3>
                <ul className="list-disc pl-5 space-y-2 text-gray-600">
                  {renderUtilization()}
                </ul>
              </div>

              {plant?.composition && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Kandungan
                  </h3>
                  <ul className="list-disc pl-5 space-y-2 text-gray-600">
                    {renderComposition()}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default PlantDetail;